import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { Typography, TextField, Button, MenuItem, Dialog, DialogTitle, DialogContent, CircularProgress, Switch } from '@material-ui/core';

import { getCgByMarginList, getMarginListPage, getMarginOrder, getSupplierByMarginList, updateMarginByCg, updateMarginByCgOverride, updateMarginByIntersectionPack, updateMarginByIntersectionUnit, updateMarginByPack, updateMarginBySupplier, updateMarginBySupplierOverride, updateMarginBySupplierPack, updateMarginBySupplierUnit, updateMarginByUnit, updateMarginOrder, updateMarginSourceChoice } from '../../api';
import { collectMarginListPage } from './collectMarginListPages';
import { commodityGroupHeaderRows } from './treeHeader';
import { checkStatus, parseJSON, handleError } from '../../../shared/api/core/common';
import { SVG_ICONS } from '../../../shared/icons/SvgIcons';
import { ErrorStatusBar, CustomTableTitle } from '../../../shared/components';
import { Toast, enqueueSnackbar, ToastSuccess, ToastError } from '../../../shared/components/Notifier';
import {
  ERROR_MSG,
  ERROR_500,
  NO_PERMISSIONS_ERROR_MSG,
} from '../../../shared/lib/Localization';

const UNITS_OF_MEASURE = ['ml', 'lt', 'floz', 'gal', 'pt', 'qt', 'g', 'kg', 'oz', 'lb', 'ct'];
const BANDS = ['#e3f2fd', '#f3e5f5', '#e8f5e9', '#fff8e1', '#fce4ec'];
const PAGE_SIZE = 5000;
const CELL_MIN_WIDTH_SAMPLE = '100% supp';
const CELL_MIN_WIDTH = `${CELL_MIN_WIDTH_SAMPLE.length}ch`;
const HEADER_MAX_LINES = 2;
const SUPPLIER_ROW_LINE_COUNT = 5;
const SUPPLIER_ROW_MIN_PX = 3 * 2 + 11 * 1.35 * SUPPLIER_ROW_LINE_COUNT;
const GRID_VISIBLE_SUPPLIER_ROWS = 10;
const HEADER_BLOCK_MIN_PX = 3 * 2 + 11 * 1.35 * HEADER_MAX_LINES;
const GRID_HEIGHT_PX = Math.ceil(HEADER_BLOCK_MIN_PX + SUPPLIER_ROW_MIN_PX * GRID_VISIBLE_SUPPLIER_ROWS);
const STEP_SUPPLIER = 'supplier';
const STEP_PACK = 'pack';
const STEP_UNIT = 'unit';
const STEP_COMMODITY_GROUP = 'commodity_group';
const DEFAULT_STEP_ORDER = [STEP_SUPPLIER, STEP_PACK, STEP_UNIT, STEP_COMMODITY_GROUP];
const SOURCE_CG = 'cg';
const SOURCE_SUPPLIER = 'supplier';
const STEP_LABELS: Record<string, string> = {
  [STEP_SUPPLIER]: 'Supplier',
  [STEP_PACK]: 'Pack',
  [STEP_UNIT]: 'Unit',
  [STEP_COMMODITY_GROUP]: 'Commodity group',
};

type MarginOrder = {
  step_order: string[];
  pack_source_first: string;
  unit_source_first: string;
};

const DEFAULT_MARGIN_ORDER: MarginOrder = {
  step_order: DEFAULT_STEP_ORDER,
  pack_source_first: SOURCE_CG,
  unit_source_first: SOURCE_CG,
};

const sameAmount = (left: any, right: any) => {
  const leftNumber = Number(left);
  const rightNumber = Number(right);
  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber === rightNumber;
  }
  return String(left) === String(right);
};

const wholePackCount = (next: string, current: string) => (
  next === '' || /^\d+$/.test(next) ? next : current
);

const unitAmount = (next: string, current: string) => (
  next === '' || /^\d+(\.\d{0,4})?$/.test(next) ? next : current
);

const marginValue = (next: string, current: string) => (
  next === '' || /^\d+(\.\d{0,2})?$/.test(next) ? next : current
);

const displayMargin = (value: any) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? String(numeric) : String(value);
};

const groupMarginValue = (group: any) => {
  const value = group && group.margin_details && group.margin_details.margin;
  if (value === null || value === undefined || value === '') {
    return null;
  }
  return value;
};

const flattenCommodityGroups = (roots: any[]) => {
  const columns: any[] = [];
  const visit = (node: any, depth: number, root: any) => {
    columns.push({
      ...node,
      depth,
      rootUuid: root.uuid,
    });
    const children = node.child_data || node.children || [];
    children.forEach((child: any) => visit(child, depth + 1, root));
  };
  (roots || []).forEach((root) => visit(root, 0, root));
  return columns;
};

const supplyingParent = (group: any) => (group && group.supplying_parent) || null;

const childLists = (node: any) => (node && (node.child_data || node.children)) || [];

const parentUuidOf = (node: any) => {
  const parent = node && node.parentCommodityGroup;
  if (!parent) {
    return null;
  }
  if (typeof parent === 'string') {
    return parent;
  }
  return parent.uuid || null;
};

const storedMargin = (node: any) => {
  const value = node && node.margin_details && node.margin_details.margin;
  if (value === null || value === undefined || value === '') {
    return null;
  }
  return value;
};

const nodesByUuid = (roots: any[]) => {
  const byUuid: Record<string, any> = {};
  const visit = (node: any) => {
    if (!node || !node.uuid) {
      return;
    }
    byUuid[node.uuid] = node;
    childLists(node).forEach(visit);
  };
  (roots || []).forEach(visit);
  return byUuid;
};

const withParentLinks = (roots: any[]) => {
  const copy = JSON.parse(JSON.stringify(roots || []));
  const visit = (node: any, parentUuid: string | null) => {
    if (!node || !node.uuid) {
      return;
    }
    if (!parentUuidOf(node) && parentUuid) {
      node.parentCommodityGroup = parentUuid;
    }
    childLists(node).forEach((child: any) => visit(child, node.uuid));
  };
  copy.forEach((root: any) => visit(root, null));
  return copy;
};

const walkedParent = (uuid: string, byUuid: Record<string, any>) => {
  let current: string | null = uuid;
  let own = true;
  const seen: Record<string, boolean> = {};
  while (current && !seen[current]) {
    seen[current] = true;
    const node = byUuid[current];
    if (!node) {
      return null;
    }
    const margin = storedMargin(node);
    if (margin !== null) {
      if (own) {
        return null;
      }
      return {
        uuid: node.uuid,
        number: node.number,
        name: node.name,
        margin,
      };
    }
    current = parentUuidOf(node);
    own = false;
  }
  return null;
};

const annotateSupplyingParents = (roots: any[]) => {
  const byUuid = nodesByUuid(roots);
  Object.keys(byUuid).forEach((uuid) => {
    byUuid[uuid].supplying_parent = walkedParent(uuid, byUuid);
  });
  return roots;
};

const patchGroupNode = (groups: any[], groupUuid: string, change: (node: any) => void, rewalk: boolean) => {
  const next = withParentLinks(groups);
  const node = nodesByUuid(next)[groupUuid];
  if (node) {
    change(node);
  }
  return rewalk ? annotateSupplyingParents(next) : next;
};

const patchSupplierMargin = (suppliers: any[], supplierUuid: string, margin: string) => (
  (suppliers || []).map((supplier) => (
    supplier.uuid === supplierUuid
      ? { ...supplier, margin_details: { ...(supplier.margin_details || {}), margin } }
      : supplier
  ))
);

const patchSupplierKeyedMargins = (
  suppliers: any[],
  supplierUuid: string,
  field: 'pack_margins' | 'unit_margins',
  nextRows: any[],
) => (
  (suppliers || []).map((supplier) => (
    supplier.uuid === supplierUuid ? { ...supplier, [field]: nextRows } : supplier
  ))
);

const overrideFor = (group: any, supplier: any) => {
  const rows = (group && group.supplier_overrides) || [];
  return rows.find((row: any) => row && row.supplier === supplier.uuid && row.margin !== null && row.margin !== '');
};

const cgOverrideFor = (group: any, supplier: any) => {
  const rows = (group && group.cg_overrides) || [];
  return rows.find((row: any) => row && row.supplier === supplier.uuid && row.margin !== null && row.margin !== '');
};

const sourceChoiceRow = (group: any, supplier: any) => {
  const rows = (group && group.source_choices) || [];
  return rows.find((row: any) => row && row.supplier === supplier.uuid) || null;
};

const chosenSource = (stored: string | null | undefined, fallback: string | undefined) => {
  if (stored === SOURCE_SUPPLIER || stored === SOURCE_CG) {
    return stored;
  }
  return fallback === SOURCE_SUPPLIER ? SOURCE_SUPPLIER : SOURCE_CG;
};

const packKeyOf = (item: any) => String(item.pack_count);
const unitKeyOf = (item: any) => `${item.amount}-${item.unit_of_measure}`;

const SOURCE_LABEL_CG = 'cg';
const SOURCE_LABEL_SUPPLIER = 'supp';
const SOURCE_LABEL_INTERSECTION = 'custom';
const ZERO_PRODUCT_GRAY = '#bdbdbd';
const INTERSECTION_ENTRY_BULLET = '• ';

const savedMarginRows = (items: any[]) =>
  (items || []).filter((item: any) => item && item.margin !== null && item.margin !== '');

const mergeKeyedMargins = (
  supplierItems: any[],
  groupItems: any[],
  keyOf: (item: any) => string,
  storedChoice: string | null | undefined,
  sourceFirst: string | undefined,
) => {
  const fromSupplier: Record<string, any> = {};
  const fromGroup: Record<string, any> = {};
  const keys: string[] = [];
  const addKey = (key: string) => {
    if (keys.indexOf(key) === -1) {
      keys.push(key);
    }
  };
  savedMarginRows(supplierItems).forEach((item: any) => {
    const key = keyOf(item);
    fromSupplier[key] = item;
    addKey(key);
  });
  savedMarginRows(groupItems).forEach((item: any) => {
    const key = keyOf(item);
    fromGroup[key] = item;
    addKey(key);
  });
  const chosen = chosenSource(storedChoice, sourceFirst);
  return keys.map((key) => {
    const supplierItem = fromSupplier[key];
    const groupItem = fromGroup[key];
    const useSupplier = supplierItem && (!groupItem || chosen === SOURCE_SUPPLIER);
    const item = useSupplier ? supplierItem : groupItem;
    return {
      ...item,
      sourceName: useSupplier ? SOURCE_LABEL_SUPPLIER : SOURCE_LABEL_CG,
      overridden: false,
    };
  });
};

const rowsForSupplier = (rows: any[], supplier: any) => (
  (rows || []).filter((item) => item && item.supplier === supplier.uuid)
);

const withIntersectionOverrides = (
  merged: any[],
  overrides: any[],
  keyOf: (item: any) => string,
) => {
  const byKey: Record<string, any> = {};
  savedMarginRows(overrides).forEach((item) => {
    byKey[keyOf(item)] = item;
  });
  const seen: Record<string, boolean> = {};
  const next = merged.map((item) => {
    const key = keyOf(item);
    seen[key] = true;
    const override = byKey[key];
    if (!override) {
      return item;
    }
    return {
      ...item,
      ...override,
      sourceName: SOURCE_LABEL_INTERSECTION,
      overridden: true,
    };
  });
  Object.keys(byKey).forEach((key) => {
    if (seen[key]) {
      return;
    }
    next.push({
      ...byKey[key],
      sourceName: SOURCE_LABEL_INTERSECTION,
      overridden: true,
    });
  });
  return next;
};

const overlappingKeysWithoutOverride = (
  supplierItems: any[],
  groupItems: any[],
  overrides: any[],
  keyOf: (item: any) => string,
) => {
  const groupKeys: Record<string, boolean> = {};
  const overrideKeys: Record<string, boolean> = {};
  savedMarginRows(groupItems).forEach((item) => {
    groupKeys[keyOf(item)] = true;
  });
  savedMarginRows(overrides).forEach((item) => {
    overrideKeys[keyOf(item)] = true;
  });
  return savedMarginRows(supplierItems).some((item) => {
    const key = keyOf(item);
    return groupKeys[key] && !overrideKeys[key];
  });
};

const percentLabel = (value: any, source: string) => {
  const shown = displayMargin(value);
  return shown === '' ? '' : `${shown}% ${source}`;
};

const otherSource = (value: string) => (value === SOURCE_SUPPLIER ? SOURCE_CG : SOURCE_SUPPLIER);

const normalizeMarginOrder = (raw: any): MarginOrder => {
  const steps = Array.isArray(raw && raw.step_order) && raw.step_order.length
    ? raw.step_order.map((step: any) => String(step))
    : DEFAULT_STEP_ORDER;
  return {
    step_order: steps,
    pack_source_first: raw && raw.pack_source_first === SOURCE_SUPPLIER ? SOURCE_SUPPLIER : SOURCE_CG,
    unit_source_first: raw && raw.unit_source_first === SOURCE_SUPPLIER ? SOURCE_SUPPLIER : SOURCE_CG,
  };
};

const swapStep = (steps: string[], index: number, direction: number) => {
  const next = steps.slice();
  const swapWith = index + direction;
  if (swapWith < 0 || swapWith >= next.length) {
    return next;
  }
  const held = next[index];
  next[index] = next[swapWith];
  next[swapWith] = held;
  return next;
};

const authTokenFrom = (userData: any) => {
  const account = userData && userData.userData && userData.userData.userData;
  return account ? account.auth_token : null;
};

const failureMessage = (error: any, fallback: string) => {
  const status = error && error.status;
  if (status === 403 || status === 401) {
    return NO_PERMISSIONS_ERROR_MSG;
  }
  if (status === 500) {
    return ERROR_500;
  }
  return fallback;
};

const MarginRowsEditor = (props: {
  rows: Array<{ key: string; label: string; labelWidth: number; margin: any; saveLabel: string; removeLabel: string; save: (margin: string) => void }>;
  canAdd: boolean;
  onAdd: () => void;
  addLabel: string;
  marginLabel: string;
  margin: string;
  onMarginChange: (value: string) => void;
  children: React.ReactNode;
}) => {
  const { rows, canAdd, onAdd, addLabel, marginLabel, margin, onMarginChange, children } = props;
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  return (
    <div>
      {rows.map((item) => {
        const draft = drafts[item.key] !== undefined ? drafts[item.key] : displayMargin(item.margin);
        return (
          <div key={item.key} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ minWidth: item.labelWidth }}>{item.label}</span>
            <TextField
              value={draft}
              type="number"
              variant="outlined"
              margin="dense"
              inputProps={{ 'aria-label': item.saveLabel }}
              onChange={(event) => setDrafts({ ...drafts, [item.key]: marginValue(event.target.value, draft) })}
              style={{ width: 72, marginRight: 4 }}
            />
            <Button size="small" color="primary" aria-label={item.saveLabel} onClick={() => item.save(draft)}>Save</Button>
            <Button size="small" aria-label={item.removeLabel} onClick={() => item.save('')}>Remove</Button>
          </div>
        );
      })}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {children}
        <TextField
          value={margin}
          type="number"
          variant="outlined"
          margin="dense"
          placeholder="Margin"
          inputProps={{ 'aria-label': marginLabel }}
          onChange={(event) => onMarginChange(marginValue(event.target.value, margin))}
          style={{ width: 72, marginRight: 4 }}
        />
        <Button size="small" color="primary" aria-label={addLabel} disabled={!canAdd} onClick={onAdd}>Add</Button>
      </div>
    </div>
  );
};

const PackMarginsEditor = (props: {
  row: any;
  scope?: string;
  onSave: (row: any, packCount: string, margin: string) => Promise<unknown>;
}) => {
  const { row, onSave, scope } = props;
  const [packCount, setPackCount] = useState('');
  const [margin, setMargin] = useState('');
  return (
    <MarginRowsEditor
      rows={savedMarginRows(row.pack_margins).map((item: any) => ({
        key: String(item.pack_count),
        label: `${item.pack_count} pk`,
        labelWidth: 72,
        margin: item.margin,
        saveLabel: `Save ${scope ? `${scope} pack margin` : 'pack margin'} ${item.pack_count}`,
        removeLabel: `Remove ${scope ? `${scope} pack margin` : 'pack margin'} ${item.pack_count}`,
        save: (value: string) => onSave(row, String(item.pack_count), value),
      }))}
      margin={margin}
      marginLabel={scope ? `${scope} pack margin` : 'Pack margin'}
      addLabel={scope ? `Add ${scope} pack margin` : 'Add pack margin'}
      onMarginChange={setMargin}
      canAdd={packCount.trim() !== '' && margin.trim() !== ''}
      onAdd={() => onSave(row, packCount, margin).then(() => {
        setPackCount('');
        setMargin('');
      }).catch(() => undefined)}
    >
      <TextField
        value={packCount}
        type="number"
        variant="outlined"
        margin="dense"
        placeholder="Pack"
        inputProps={{ 'aria-label': scope ? `${scope} pack count` : 'Pack count' }}
        onChange={(event) => setPackCount((current) => wholePackCount(event.target.value, current))}
        style={{ width: 144, marginRight: 4 }}
      />
      <TextField
        value="PK"
        variant="outlined"
        margin="dense"
        InputProps={{ readOnly: true }}
        style={{ width: 56, marginRight: 4 }}
      />
    </MarginRowsEditor>
  );
};

const UnitMarginsEditor = (props: {
  row: any;
  scope?: string;
  onSave: (row: any, amount: string, unitOfMeasure: string, margin: string) => Promise<unknown>;
}) => {
  const { row, onSave, scope } = props;
  const [amount, setAmount] = useState('');
  const [unitOfMeasure, setUnitOfMeasure] = useState('floz');
  const [margin, setMargin] = useState('');
  return (
    <MarginRowsEditor
      rows={savedMarginRows(row.unit_margins).map((item: any) => ({
        key: `${item.amount}-${item.unit_of_measure}`,
        label: `${displayMargin(item.amount)} ${item.unit_of_measure}`,
        labelWidth: 96,
        margin: item.margin,
        saveLabel: `Save ${scope ? `${scope} unit margin` : 'unit margin'} ${displayMargin(item.amount)} ${item.unit_of_measure}`,
        removeLabel: `Remove ${scope ? `${scope} unit margin` : 'unit margin'} ${displayMargin(item.amount)} ${item.unit_of_measure}`,
        save: (value: string) => onSave(row, item.amount, item.unit_of_measure, value),
      }))}
      margin={margin}
      marginLabel={scope ? `${scope} unit margin` : 'Unit margin'}
      addLabel={scope ? `Add ${scope} unit margin` : 'Add unit margin'}
      onMarginChange={setMargin}
      canAdd={amount.trim() !== '' && margin.trim() !== ''}
      onAdd={() => onSave(row, amount, unitOfMeasure, margin).then(() => {
        setAmount('');
        setMargin('');
      }).catch(() => undefined)}
    >
      <TextField
        value={amount}
        type="number"
        variant="outlined"
        margin="dense"
        placeholder="Amount"
        inputProps={{ 'aria-label': scope ? `${scope} unit amount` : 'Unit amount' }}
        onChange={(event) => setAmount((current) => unitAmount(event.target.value, current))}
        style={{ width: 160, marginRight: 4 }}
      />
      <TextField
        select
        value={unitOfMeasure}
        variant="outlined"
        margin="dense"
        onChange={(event) => setUnitOfMeasure(event.target.value)}
        style={{ width: 88, marginRight: 4 }}
      >
        {UNITS_OF_MEASURE.map((code) => (
          <MenuItem key={code} value={code}>{code}</MenuItem>
        ))}
      </TextField>
    </MarginRowsEditor>
  );
};

const SupplierMarginLine = (props: {
  supplier: any;
  onSave: (supplier: any, margin: string) => Promise<unknown>;
}) => {
  const { supplier, onSave } = props;
  const saved = displayMargin(supplier.margin_details && supplier.margin_details.margin);
  const [draft, setDraft] = useState(saved);
  const hasMargin = saved !== '';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
      <span style={{ minWidth: 220 }}>{supplier.number} {supplier.name}</span>
      <TextField
        value={draft}
        type="number"
        variant="outlined"
        margin="dense"
        placeholder="%"
        inputProps={{ 'aria-label': `Supplier margin for ${supplier.name}` }}
        onChange={(event) => setDraft((current) => marginValue(event.target.value, current))}
        style={{ width: 88 }}
      />
      <Button size="small" color="primary" onClick={() => onSave(supplier, draft)}>
        {hasMargin ? 'Save' : 'Add'}
      </Button>
      <Button
        size="small"
        aria-label={`Clear supplier margin for ${supplier.name}`}
        onClick={() => {
          if (saved === '') {
            return;
          }
          onSave(supplier, '');
        }}
      >
        Clear
      </Button>
    </div>
  );
};

const productCountFor = (group: any, supplier: any) => {
  const counts = group && group.product_counts;
  if (!counts) {
    return 0;
  }
  const numeric = Number(counts[supplier.uuid]);
  return Number.isFinite(numeric) ? numeric : 0;
};

const marginLineStyle: React.CSSProperties = {
  whiteSpace: 'nowrap',
  minHeight: '1.35em',
};

const gridCellStyle: React.CSSProperties = {
  boxSizing: 'border-box',
  display: 'table-cell',
  padding: '3px 6px',
  fontSize: 11,
  lineHeight: 1.35,
  textAlign: 'left',
  verticalAlign: 'top',
};

const headerNameClamp: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: HEADER_MAX_LINES,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  whiteSpace: 'normal',
};

const rateText = (items: any[], labelOf: (item: any) => string) => (
  items.length
    ? items.map((item, index) => (
      <span key={`${labelOf(item)}-${index}`} style={{ fontWeight: item.overridden ? 700 : 400 }}>
        {index ? ' ' : ''}{INTERSECTION_ENTRY_BULLET}{percentLabel(item.margin, `${labelOf(item)} ${item.sourceName}`)}
      </span>
    ))
    : `${INTERSECTION_ENTRY_BULLET}—`
);

const CellLine = (props: { supplier: any; group: any; marginOrder?: MarginOrder }) => {
  const { supplier, group } = props;
  const marginOrder = props.marginOrder || DEFAULT_MARGIN_ORDER;
  const override = overrideFor(group, supplier);
  const supplierMargin = supplier.margin_details && supplier.margin_details.margin;
  const choice = sourceChoiceRow(group, supplier);
  const packs = withIntersectionOverrides(
    mergeKeyedMargins(
      supplier.pack_margins,
      group.pack_margins,
      packKeyOf,
      choice && choice.pack_source,
      marginOrder.pack_source_first,
    ),
    rowsForSupplier(group && group.intersection_pack_overrides, supplier),
    packKeyOf,
  );
  const units = withIntersectionOverrides(
    mergeKeyedMargins(
      supplier.unit_margins,
      group.unit_margins,
      unitKeyOf,
      choice && choice.unit_source,
      marginOrder.unit_source_first,
    ),
    rowsForSupplier(group && group.intersection_unit_overrides, supplier),
    unitKeyOf,
  );
  const cgOverride = cgOverrideFor(group, supplier);
  const ownMargin = groupMarginValue(group);
  const parent = supplyingParent(group);
  const lead = override
    ? percentLabel(override.margin, 'supp ov')
    : (percentLabel(supplierMargin, 'supp') || '—');
  const count = productCountFor(group, supplier);
  const quiet = count === 0 ? ZERO_PRODUCT_GRAY : undefined;
  const groupLine = cgOverride
    ? percentLabel(cgOverride.margin, 'cg ov')
    : ownMargin !== null
    ? percentLabel(ownMargin, 'cg')
    : parent
      ? (
        <span style={{ color: quiet || '#6a1b9a' }}>{percentLabel(parent.margin, `↑${parent.number}`)}</span>
      )
      : '—';
  const lines: Record<string, React.ReactNode> = {
    [STEP_SUPPLIER]: (
      <div
        key={STEP_SUPPLIER}
        data-margin-line="supplier"
        title={override ? 'supplier override' : 'supplier margin'}
        style={{
          ...marginLineStyle,
          color: quiet || (override ? '#0d47a1' : '#616161'),
          fontWeight: override ? 700 : 400,
        }}
      >
        {INTERSECTION_ENTRY_BULLET}{lead}
      </div>
    ),
    [STEP_PACK]: (
      <div key={STEP_PACK} data-margin-line="packs" style={{ ...marginLineStyle, color: quiet }}>
        {rateText(packs, (item) => `${item.pack_count}pk`)}
      </div>
    ),
    [STEP_UNIT]: (
      <div key={STEP_UNIT} data-margin-line="units" style={{ ...marginLineStyle, color: quiet }}>
        {rateText(units, (item) => `${displayMargin(item.amount)}${item.unit_of_measure}`)}
      </div>
    ),
    [STEP_COMMODITY_GROUP]: (
      <div key={STEP_COMMODITY_GROUP} data-margin-line="group" style={{ ...marginLineStyle, color: quiet }}>
        {INTERSECTION_ENTRY_BULLET}{groupLine}
      </div>
    ),
  };
  const stepOrder = (marginOrder.step_order && marginOrder.step_order.length)
    ? marginOrder.step_order
    : DEFAULT_STEP_ORDER;

  return (
    <div>
      <div
        data-margin-line="products"
        style={{ ...marginLineStyle, fontWeight: 700, color: count === 0 ? ZERO_PRODUCT_GRAY : '#1b5e20' }}
      >
        {INTERSECTION_ENTRY_BULLET}{count} {count === 1 ? 'product' : 'products'}
      </div>
      {stepOrder.map((step) => lines[step]).filter(Boolean)}
    </div>
  );
};

function InstantDialogTransition(props: { children?: React.ReactNode }) {
  return <div>{props.children}</div>;
}

const settled = () => Promise.resolve();

const supplierMarginSummary = (suppliers: any[]) => {
  const withMargin = suppliers.filter((supplier) => (
    displayMargin(supplier.margin_details && supplier.margin_details.margin) !== ''
  )).length;
  return `${withMargin} of ${suppliers.length} have a margin`;
};

const PanelSpinner = (props: { label: string }) => (
  <CircularProgress size={16} aria-label={props.label} style={{ marginLeft: 8 }} />
);

const barButtonStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  background: '#fafafa',
  border: 0,
  borderBottom: '1px solid #eee',
  padding: '6px 8px',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
};

export const MarginByCgView = (props: {
  suppliers: any[];
  commodityGroups: any[];
  isLoading?: boolean;
  marginOrder?: MarginOrder;
  onChangeSupplierMargin?: (supplier: any, margin: string) => Promise<unknown>;
  onChangePackMargin?: (row: any, packCount: string, margin: string) => Promise<unknown>;
  onChangeUnitMargin?: (row: any, amount: string, unitOfMeasure: string, margin: string) => Promise<unknown>;
  onChangeSupplierPackMargin?: (row: any, packCount: string, margin: string) => Promise<unknown>;
  onChangeSupplierUnitMargin?: (row: any, amount: string, unitOfMeasure: string, margin: string) => Promise<unknown>;
  onChangeCgMargin?: (group: any, margin: string) => Promise<unknown>;
  onChangeOverride?: (group: any, supplier: any, margin: string) => Promise<unknown>;
  onChangeCgOverride?: (group: any, supplier: any, margin: string) => Promise<unknown>;
  onChangeIntersectionPack?: (group: any, supplier: any, packCount: string, margin: string) => Promise<unknown>;
  onChangeIntersectionUnit?: (group: any, supplier: any, amount: string, unitOfMeasure: string, margin: string) => Promise<unknown>;
  onChangeSourceChoice?: (group: any, supplier: any, source: string) => Promise<unknown>;
  onChangeMarginOrder?: (next: MarginOrder) => Promise<unknown>;
}) => {
  const suppliers = props.suppliers || [];
  const groups = flattenCommodityGroups(props.commodityGroups || []);
  const isLoading = !!props.isLoading;
  const marginOrder = props.marginOrder || DEFAULT_MARGIN_ORDER;
  const changeSupplierMargin = props.onChangeSupplierMargin || settled;
  const changePackMargin = props.onChangePackMargin || settled;
  const changeUnitMargin = props.onChangeUnitMargin || settled;
  const changeSupplierPackMargin = props.onChangeSupplierPackMargin || settled;
  const changeSupplierUnitMargin = props.onChangeSupplierUnitMargin || settled;
  const changeCgMargin = props.onChangeCgMargin || settled;
  const changeOverride = props.onChangeOverride || settled;
  const changeCgOverride = props.onChangeCgOverride || settled;
  const changeIntersectionPack = props.onChangeIntersectionPack || settled;
  const changeIntersectionUnit = props.onChangeIntersectionUnit || settled;
  const changeSourceChoice = props.onChangeSourceChoice || settled;
  const changeMarginOrder = props.onChangeMarginOrder || settled;
  const [selected, setSelected] = useState<{ supplierUuid: string; groupUuid: string } | null>(null);
  const [suppliersOpen, setSuppliersOpen] = useState(true);
  const [sharedOpen, setSharedOpen] = useState(true);
  const [orderOpen, setOrderOpen] = useState(false);
  const [sharedSupplierUuid, setSharedSupplierUuid] = useState('');
  const [sharedGroupUuid, setSharedGroupUuid] = useState('');
  const collapsedSupplierSummary = supplierMarginSummary(suppliers);
  const supplierRows: any[][] = [];
  suppliers.forEach((supplier, index) => {
    if (index % 2 === 0) {
      supplierRows.push([supplier]);
    } else {
      supplierRows[supplierRows.length - 1].push(supplier);
    }
  });
  const selectedSupplier = suppliers.find((supplier) => selected && supplier.uuid === selected.supplierUuid);
  const selectedGroup = groups.find((group) => selected && group.uuid === selected.groupUuid);
  const sharedSupplier = suppliers.find((supplier) => supplier.uuid === sharedSupplierUuid);
  const sharedGroup = groups.find((group) => group.uuid === sharedGroupUuid);
  const headerRows = commodityGroupHeaderRows(groups);
  const rootOrder: string[] = [];
  groups.forEach((column) => {
    if (rootOrder.indexOf(column.rootUuid) === -1) {
      rootOrder.push(column.rootUuid);
    }
  });

  return (
    <div>
      <div style={{ margin: '12px 0', border: '1px solid #cfd8dc', background: '#fff' }}>
        <button type="button" onClick={() => setSuppliersOpen((open) => !open)} style={barButtonStyle}>
          {suppliersOpen ? '▾' : '▸'} Supplier margins · {collapsedSupplierSummary}
          {isLoading ? <PanelSpinner label="Loading supplier margins" /> : null}
        </button>
        {suppliersOpen ? (
          <div style={{ padding: '8px 12px' }}>
            {supplierRows.map((row) => (
              <div key={row.map((supplier) => supplier.uuid).join('-')} style={{ display: 'flex', gap: 16 }}>
                {row.map((supplier) => (
                  <div key={`${supplier.uuid}-${displayMargin(supplier.margin_details && supplier.margin_details.margin)}`} style={{ flex: '1 1 0', minWidth: 0 }}>
                    <SupplierMarginLine
                      supplier={supplier}
                      onSave={changeSupplierMargin}
                    />
                  </div>
                ))}
              </div>
            ))}
            {!isLoading && suppliers.length === 0 ? (
              <Typography variant="body2">No suppliers.</Typography>
            ) : null}
          </div>
        ) : null}
      </div>
      <div
        data-margin-shared-rates=""
        style={{ marginBottom: 12, border: '1px solid #cfd8dc', background: '#fff' }}
      >
        <button type="button" onClick={() => setSharedOpen((open) => !open)} style={barButtonStyle}>
          {sharedOpen ? '▾' : '▸'} Pack and unit margins
          {isLoading ? <PanelSpinner label="Loading pack and unit margins" /> : null}
        </button>
        {sharedOpen ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, padding: '8px 12px' }}>
            <div style={{ paddingRight: 16 }}>
              <Typography variant="subtitle2">pack and unit margins by supplier</Typography>
              <TextField
                select
                value={sharedSupplierUuid}
                variant="outlined"
                margin="dense"
                inputProps={{ 'aria-label': 'Pick supplier for pack and unit margins' }}
                onChange={(event) => setSharedSupplierUuid(event.target.value)}
                style={{ minWidth: 220, marginBottom: 8 }}
              >
                <MenuItem value="">Select a supplier</MenuItem>
                {suppliers.map((supplier) => (
                  <MenuItem key={supplier.uuid} value={supplier.uuid}>{supplier.number} {supplier.name}</MenuItem>
                ))}
              </TextField>
              {sharedSupplier ? (
                <div>
                  <Typography variant="caption">Applies to every commodity group for this supplier.</Typography>
                  <PackMarginsEditor row={sharedSupplier} onSave={changeSupplierPackMargin} />
                  <UnitMarginsEditor row={sharedSupplier} onSave={changeSupplierUnitMargin} />
                </div>
              ) : null}
            </div>
            <div style={{ borderLeft: '2px solid #455a64', paddingLeft: 16 }}>
              <Typography variant="subtitle2">pack and unit margins by commodity group</Typography>
              <TextField
                select
                value={sharedGroupUuid}
                variant="outlined"
                margin="dense"
                inputProps={{ 'aria-label': 'Pick commodity group for pack and unit margins' }}
                onChange={(event) => setSharedGroupUuid(event.target.value)}
                style={{ minWidth: 220, marginBottom: 8 }}
              >
                <MenuItem value="">Select a commodity group</MenuItem>
                {groups.map((group) => (
                  <MenuItem key={group.uuid} value={group.uuid}>
                    {group.depth ? `${'-'.repeat(group.depth)} ` : ''}{group.number} {group.name}
                  </MenuItem>
                ))}
              </TextField>
              {sharedGroup ? (
                <div>
                  <Typography variant="caption">Applies to every supplier for this commodity group.</Typography>
                  <CommodityGroupMarginEditor group={sharedGroup} onSave={changeCgMargin} />
                  <PackMarginsEditor row={sharedGroup} scope="commodity group" onSave={changePackMargin} />
                  <UnitMarginsEditor row={sharedGroup} scope="commodity group" onSave={changeUnitMargin} />
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
      <GroupEditor
        supplier={selectedSupplier}
        group={selectedGroup}
        marginOrder={marginOrder}
        isLoading={isLoading}
        onSaveOverride={changeOverride}
        onSaveCgOverride={changeCgOverride}
        onSavePack={changeIntersectionPack}
        onSaveUnit={changeIntersectionUnit}
        onSaveSourceChoice={changeSourceChoice}
      />
      <div style={{ margin: '0 0 12px', border: '1px solid #cfd8dc', background: '#fff' }}>
        <button type="button" onClick={() => setOrderOpen((open) => !open)} style={barButtonStyle}>
          {orderOpen ? '▾' : '▸'} Margin order
          {isLoading ? <PanelSpinner label="Loading margin order" /> : null}
        </button>
        {orderOpen ? (
          <div data-margin-order="" style={{ padding: '8px 12px' }}>
            {marginOrder.step_order.map((step, index) => (
              <div key={step} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ minWidth: 160 }}>{index + 1}. {STEP_LABELS[step] || step}</span>
                <Button
                  size="small"
                  aria-label={`move ${step} up`}
                  disabled={index === 0}
                  onClick={() => changeMarginOrder({
                    ...marginOrder,
                    step_order: swapStep(marginOrder.step_order, index, -1),
                  })}
                >
                  move up
                </Button>
                <Button
                  size="small"
                  aria-label={`move ${step} down`}
                  disabled={index === marginOrder.step_order.length - 1}
                  onClick={() => changeMarginOrder({
                    ...marginOrder,
                    step_order: swapStep(marginOrder.step_order, index, 1),
                  })}
                >
                  move down
                </Button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <Button
                size="small"
                aria-label="Swap pack source first"
                onClick={() => changeMarginOrder({
                  ...marginOrder,
                  pack_source_first: otherSource(marginOrder.pack_source_first),
                })}
              >
                Pack source first: {marginOrder.pack_source_first}
              </Button>
              <Button
                size="small"
                aria-label="Swap unit source first"
                onClick={() => changeMarginOrder({
                  ...marginOrder,
                  unit_source_first: otherSource(marginOrder.unit_source_first),
                })}
              >
                Unit source first: {marginOrder.unit_source_first}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
      <div style={{ border: '1px solid #cfd8dc', background: '#fff' }}>
        <div style={barButtonStyle}>
          Margin grid
          {isLoading ? <PanelSpinner label="Loading margin grid" /> : null}
        </div>
      <div data-margin-grid-scroll="" style={{ height: GRID_HEIGHT_PX, flexShrink: 0, minWidth: 0, overflow: 'auto' }}>
          {/* VerifyImportedData/style.css sets thead and tbody to display:block.
              That sizes the header and the body as two grids, so these displays
              put them back in one table. Separate borders keep a sticky supplier
              name from shifting the group columns. The header sticks as one
              block: a sticky top on each rowspan cell leaves a white band
              under the group names. */}
          <table style={{ display: 'table', borderCollapse: 'separate', borderSpacing: 0, width: 'max-content', background: '#fff' }}>
            <thead style={{ display: 'table-header-group', position: 'sticky', top: 0, zIndex: 3, background: '#fff' }}>
              {headerRows.map((row, depth) => (
                <tr key={depth} style={{ display: 'table-row' }}>
                  {depth === 0 ? (
                    <th
                      rowSpan={Math.max(headerRows.length, 1)}
                      style={{
                        ...gridCellStyle,
                        position: 'sticky',
                        left: 0,
                        zIndex: 4,
                        background: '#eceff1',
                        borderBottom: '1px solid #cfd8dc',
                        borderRight: '1px solid #cfd8dc',
                        minWidth: 120,
                        whiteSpace: 'nowrap',
                        verticalAlign: 'bottom',
                      }}
                    >
                      Supplier
                    </th>
                  ) : null}
                  {row.map((cell) => {
                    const rootIndex = rootOrder.indexOf(cell.rootId);
                    return (
                      <th
                        key={cell.key}
                        colSpan={cell.colSpan}
                        rowSpan={cell.rowSpan}
                        style={{
                          ...gridCellStyle,
                          background: BANDS[(rootIndex < 0 ? 0 : rootIndex) % BANDS.length],
                          fontWeight: cell.own ? 600 : 500,
                          borderBottom: '1px solid #cfd8dc',
                          borderRight: '1px solid #b0bec5',
                          whiteSpace: 'normal',
                          minWidth: CELL_MIN_WIDTH,
                          verticalAlign: 'bottom',
                        }}
                      >
                        <span style={headerNameClamp}>
                          {cell.own ? `${cell.number} ${cell.name}` : cell.name}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody style={{ display: 'table-row-group', maxWidth: 'none', overflow: 'visible' }}>
              {suppliers.map((supplier) => (
                <tr key={supplier.uuid} style={{ display: 'table-row' }}>
                  <th style={{ ...gridCellStyle, position: 'sticky', left: 0, zIndex: 1, background: '#fff', fontSize: 12, fontWeight: 600, borderBottom: '1px solid #eee', borderRight: '1px solid #cfd8dc', whiteSpace: 'nowrap' }}>
                    {supplier.name}
                  </th>
                  {groups.map((group) => {
                    const isSelected = selected
                      && selected.supplierUuid === supplier.uuid
                      && selected.groupUuid === group.uuid;
                    return (
                      <td
                        key={group.uuid}
                        onClick={() => {
                          setSelected({ supplierUuid: supplier.uuid, groupUuid: group.uuid });
                          setSharedSupplierUuid(supplier.uuid);
                          setSharedGroupUuid(group.uuid);
                        }}
                        style={{
                          ...gridCellStyle,
                          borderBottom: '1px solid #eee',
                          borderRight: '1px solid #f5f5f5',
                          cursor: 'pointer',
                          background: '#fff',
                          minWidth: CELL_MIN_WIDTH,
                          boxShadow: isSelected ? 'inset 0 0 0 2px #1565c0' : 'none',
                        }}
                      >
                        <CellLine supplier={supplier} group={group} marginOrder={marginOrder} />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export const MarginByCgScreen = (props: any) => {
  const [isErrorStatusVisible, setErrorStatusVisible] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [marginOrder, setMarginOrder] = useState<MarginOrder>(DEFAULT_MARGIN_ORDER);
  const [helpOpen, setHelpOpen] = useState(false);
  const businessId = props.userData && props.userData.selectedBusiness && props.userData.selectedBusiness.business_id;

  useEffect(() => {
    if (!businessId) {
      setSuppliers([]);
      setGroups([]);
      setMarginOrder(DEFAULT_MARGIN_ORDER);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const loadEveryPage = async (firstRequest: Promise<any>) => {
      let rows: any[] = [];
      let request: Promise<any> | null = firstRequest;
      const seen = new Set<string>();
      while (request) {
        const data = await request.catch(handleError).then(checkStatus).then(parseJSON);
        const page = collectMarginListPage(rows, data);
        rows = page.rows;
        if (!page.next || seen.has(page.next)) {
          break;
        }
        seen.add(page.next);
        request = getMarginListPage(page.next);
      }
      return rows;
    };
    Promise.all([
      loadEveryPage(getSupplierByMarginList(businessId, 1, PAGE_SIZE, '', 'name')),
      loadEveryPage(getCgByMarginList(businessId, 1, PAGE_SIZE, '', 'number')),
      Promise.resolve(getMarginOrder(businessId)).then(checkStatus).then(parseJSON).catch(() => null),
    ])
      .then(([supplierRows, groupRows, order]) => {
        if (cancelled) {
          return;
        }
        setSuppliers(supplierRows);
        setGroups(groupRows);
        if (order) {
          setMarginOrder(normalizeMarginOrder(order));
        }
        setErrorStatusVisible(false);
        setLoading(false);
      })
      .catch((error: any) => {
        if (cancelled) {
          return;
        }
        if (error && (error.status === 403 || error.status === 401 || error.status === 500)) {
          setErrorStatusVisible(true);
        } else {
          setErrorStatusVisible(false);
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  const postMargin = (
    request: (data: any) => Promise<any>,
    payload: any,
    successMessage: string,
    failureFallback: string,
    apply: () => void,
  ) => {
    const authToken = authTokenFrom(props.userData);
    if (!authToken) {
      return Promise.reject();
    }
    const body = {
      ...payload,
      business_details: props.userData.selectedBusiness,
      user_details: { auth_token: authToken },
    };
    return request(body)
      .catch(handleError)
      .then(checkStatus)
      .then(parseJSON)
      .then(() => {
        apply();
        props.showToast({ message: successMessage, options: ToastSuccess });
      })
      .catch((error: any) => {
        props.showToast({ message: failureMessage(error, failureFallback), options: ToastError });
        return Promise.reject(error);
      });
  };

  const changeSupplierMargin = (supplier: any, margin: string) => postMargin(
    updateMarginBySupplier,
    {
      uuid: supplier.uuid,
      margin_details: { ...(supplier.margin_details || {}), margin },
    },
    margin === '' ? 'Supplier margin removed.' : 'Supplier margin updated successfully!',
    'Failed to update supplier margin.',
    () => setSuppliers((current) => patchSupplierMargin(current, supplier.uuid, margin)),
  );

  const changePackMargin = (row: any, packCount: string, margin: string) => postMargin(
    updateMarginByPack,
    {
      uuid: row.uuid,
      pack_count: packCount,
      margin_details: { margin },
    },
    margin === '' ? 'Pack margin removed.' : 'Pack margin saved.',
    'Failed to update pack margin.',
    () => setGroups((current) => patchGroupNode(current, row.uuid, (node) => {
      const kept = (node.pack_margins || []).filter((item: any) => String(item.pack_count) !== String(packCount));
      node.pack_margins = margin === '' ? kept : kept.concat([{ pack_count: packCount, margin }]);
    }, false)),
  );

  const changeUnitMargin = (row: any, amount: string, unitOfMeasure: string, margin: string) => postMargin(
    updateMarginByUnit,
    {
      uuid: row.uuid,
      amount,
      unit_of_measure: unitOfMeasure,
      margin_details: { margin },
    },
    margin === '' ? 'Unit margin removed.' : 'Unit margin saved.',
    'Failed to update unit margin.',
    () => setGroups((current) => patchGroupNode(current, row.uuid, (node) => {
      const kept = (node.unit_margins || []).filter((item: any) => (
        String(item.amount) !== String(amount) || item.unit_of_measure !== unitOfMeasure
      ));
      node.unit_margins = margin === ''
        ? kept
        : kept.concat([{ amount, unit_of_measure: unitOfMeasure, margin }]);
    }, false)),
  );

  const changeCgMargin = (group: any, margin: string) => postMargin(
    updateMarginByCg,
    {
      uuid: group.uuid,
      margin_details: { margin },
    },
    margin === '' ? 'Margin removed.' : 'Margin updated successfully!',
    'Failed to update Margin.',
    () => setGroups((current) => patchGroupNode(current, group.uuid, (node) => {
      node.margin_details = { ...(node.margin_details || {}), margin };
    }, true)),
  );

  const changeOverride = (group: any, supplier: any, margin: string) => postMargin(
    updateMarginBySupplierOverride,
    {
      uuid: group.uuid,
      supplier_uuid: supplier.uuid,
      margin_details: { margin },
    },
    margin === '' ? 'Supplier margin override cleared.' : 'Supplier margin override saved.',
    'Failed to update supplier margin override.',
    () => setGroups((current) => patchGroupNode(current, group.uuid, (node) => {
      const kept = (node.supplier_overrides || []).filter((item: any) => item && item.supplier !== supplier.uuid);
      node.supplier_overrides = margin === '' ? kept : kept.concat([{ supplier: supplier.uuid, margin }]);
    }, false)),
  );

  const changeCgOverride = (group: any, supplier: any, margin: string) => postMargin(
    updateMarginByCgOverride,
    {
      uuid: group.uuid,
      supplier_uuid: supplier.uuid,
      margin_details: { margin },
    },
    margin === '' ? 'Commodity group margin override cleared.' : 'Commodity group margin override saved.',
    'Failed to update commodity group margin override.',
    () => setGroups((current) => patchGroupNode(current, group.uuid, (node) => {
      const kept = (node.cg_overrides || []).filter((item: any) => item && item.supplier !== supplier.uuid);
      node.cg_overrides = margin === '' ? kept : kept.concat([{ supplier: supplier.uuid, margin }]);
    }, false)),
  );

  const changeIntersectionPack = (group: any, supplier: any, packCount: string, margin: string) => postMargin(
    updateMarginByIntersectionPack,
    {
      uuid: group.uuid,
      supplier_uuid: supplier.uuid,
      pack_count: packCount,
      margin_details: { margin },
    },
    margin === '' ? 'Pack override cleared.' : 'Pack override saved.',
    'Failed to update pack override.',
    () => setGroups((current) => patchGroupNode(current, group.uuid, (node) => {
      const kept = (node.intersection_pack_overrides || []).filter((item: any) => (
        !item || item.supplier !== supplier.uuid || String(item.pack_count) !== String(packCount)
      ));
      node.intersection_pack_overrides = margin === ''
        ? kept
        : kept.concat([{ supplier: supplier.uuid, pack_count: packCount, margin }]);
    }, false)),
  );

  const changeIntersectionUnit = (group: any, supplier: any, amount: string, unitOfMeasure: string, margin: string) => postMargin(
    updateMarginByIntersectionUnit,
    {
      uuid: group.uuid,
      supplier_uuid: supplier.uuid,
      amount,
      unit_of_measure: unitOfMeasure,
      margin_details: { margin },
    },
    margin === '' ? 'Unit override cleared.' : 'Unit override saved.',
    'Failed to update unit override.',
    () => setGroups((current) => patchGroupNode(current, group.uuid, (node) => {
      const kept = (node.intersection_unit_overrides || []).filter((item: any) => (
        !item || item.supplier !== supplier.uuid || !sameAmount(item.amount, amount) || item.unit_of_measure !== unitOfMeasure
      ));
      node.intersection_unit_overrides = margin === ''
        ? kept
        : kept.concat([{ supplier: supplier.uuid, amount, unit_of_measure: unitOfMeasure, margin }]);
    }, false)),
  );

  const changeSupplierPackMargin = (row: any, packCount: string, margin: string) => postMargin(
    updateMarginBySupplierPack,
    {
      uuid: row.uuid,
      pack_count: packCount,
      margin_details: { margin },
    },
    margin === '' ? 'Pack margin removed.' : 'Pack margin saved.',
    'Failed to update pack margin.',
    () => setSuppliers((current) => {
      const supplier = current.find((item) => item.uuid === row.uuid);
      const kept = ((supplier && supplier.pack_margins) || []).filter((item: any) => String(item.pack_count) !== String(packCount));
      return patchSupplierKeyedMargins(
        current,
        row.uuid,
        'pack_margins',
        margin === '' ? kept : kept.concat([{ pack_count: packCount, margin }]),
      );
    }),
  );

  const changeSupplierUnitMargin = (row: any, amount: string, unitOfMeasure: string, margin: string) => postMargin(
    updateMarginBySupplierUnit,
    {
      uuid: row.uuid,
      amount,
      unit_of_measure: unitOfMeasure,
      margin_details: { margin },
    },
    margin === '' ? 'Unit margin removed.' : 'Unit margin saved.',
    'Failed to update unit margin.',
    () => setSuppliers((current) => {
      const supplier = current.find((item) => item.uuid === row.uuid);
      const kept = ((supplier && supplier.unit_margins) || []).filter((item: any) => (
        String(item.amount) !== String(amount) || item.unit_of_measure !== unitOfMeasure
      ));
      return patchSupplierKeyedMargins(
        current,
        row.uuid,
        'unit_margins',
        margin === '' ? kept : kept.concat([{ amount, unit_of_measure: unitOfMeasure, margin }]),
      );
    }),
  );

  const changeSourceChoice = (group: any, supplier: any, source: string) => postMargin(
    updateMarginSourceChoice,
    {
      supplier_uuid: supplier.uuid,
      commoditygroup_uuid: group.uuid,
      pack_source: source,
      unit_source: source,
    },
    source === '' ? 'Pack and unit source cleared.' : 'Pack and unit source saved.',
    'Failed to update pack and unit source.',
    () => setGroups((current) => patchGroupNode(current, group.uuid, (node) => {
      const kept = (node.source_choices || []).filter((item: any) => item && item.supplier !== supplier.uuid);
      node.source_choices = source === ''
        ? kept
        : kept.concat([{ supplier: supplier.uuid, pack_source: source, unit_source: source }]);
    }, false)),
  );

  const changeOrder = (next: MarginOrder) => postMargin(
    updateMarginOrder,
    {
      step_order: next.step_order,
      pack_source_first: next.pack_source_first,
      unit_source_first: next.unit_source_first,
    },
    'Margin order updated.',
    'Failed to update margin order.',
    () => setMarginOrder(next),
  );

  return (
    <div>
      <ErrorStatusBar isVisible={isErrorStatusVisible} />
      <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <CustomTableTitle
          history={props.history}
          title={'Margin rules'}
          icon={SVG_ICONS.reports}
        />
        <Button aria-label="Margin rules help" onClick={() => setHelpOpen(true)}>?</Button>
      </div>
      <Dialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        disablePortal
        hideBackdrop
        disableScrollLock
        TransitionComponent={InstantDialogTransition}
        BackdropComponent={InstantDialogTransition}
      >
        <DialogTitle>Margin rules</DialogTitle>
        <DialogContent>
          {marginOrder.step_order.map((step, index) => (
            <Typography key={step} variant="body2">{index + 1}. {STEP_LABELS[step] || step}</Typography>
          ))}
          <Typography variant="body2" style={{ marginTop: 8 }}>Pack source first: {marginOrder.pack_source_first}</Typography>
          <Typography variant="body2">Unit source first: {marginOrder.unit_source_first}</Typography>
        </DialogContent>
      </Dialog>
      {isErrorStatusVisible ? <Typography variant="body2">{ERROR_MSG}</Typography> : null}
      <MarginByCgView
        suppliers={suppliers}
        commodityGroups={groups}
        isLoading={isLoading}
        marginOrder={marginOrder}
        onChangeSupplierMargin={changeSupplierMargin}
        onChangePackMargin={changePackMargin}
        onChangeUnitMargin={changeUnitMargin}
        onChangeSupplierPackMargin={changeSupplierPackMargin}
        onChangeSupplierUnitMargin={changeSupplierUnitMargin}
        onChangeCgMargin={changeCgMargin}
        onChangeOverride={changeOverride}
        onChangeCgOverride={changeCgOverride}
        onChangeIntersectionPack={changeIntersectionPack}
        onChangeIntersectionUnit={changeIntersectionUnit}
        onChangeSourceChoice={changeSourceChoice}
        onChangeMarginOrder={changeOrder}
      />
    </div>
  );
};

const CommodityGroupMarginEditor = (props: {
  group: any;
  onSave: (group: any, margin: string) => Promise<unknown>;
}) => {
  const { group, onSave } = props;
  const [draft, setDraft] = useState(displayMargin(groupMarginValue(group)));

  useEffect(() => {
    setDraft(displayMargin(groupMarginValue(group)));
  }, [group && group.uuid, group && group.margin_details && group.margin_details.margin]);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <TextField
        label="Margin %"
        value={draft}
        inputProps={{ 'aria-label': 'Commodity group margin' }}
        onChange={(event) => setDraft((current) => marginValue(event.target.value, current))}
        style={{ width: 120 }}
      />
      <Button size="small" color="primary" aria-label="Save commodity group margin" onClick={() => onSave(group, draft)}>
        Save commodity group margin
      </Button>
    </div>
  );
};

const OverrideLine = (props: {
  label: string;
  margin: any;
  saveLabel: string;
  saveButtonLabel?: string;
  clearLabel: string;
  onSave: (margin: string) => Promise<unknown>;
  onClear: () => Promise<unknown>;
}) => {
  const saved = displayMargin(props.margin);
  const [draft, setDraft] = useState(saved);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <span style={{ minWidth: 72 }}>{props.label}</span>
      <TextField
        label="Margin %"
        value={draft}
        inputProps={{ 'aria-label': props.saveLabel }}
        onChange={(event) => setDraft((current) => marginValue(event.target.value, current))}
        style={{ width: 120 }}
      />
      {props.saveButtonLabel ? (
        <button type="button" aria-label={props.saveButtonLabel} onClick={() => props.onSave(draft)}>
          Save
        </button>
      ) : (
        <Button size="small" color="primary" onClick={() => props.onSave(draft)}>
          Save
        </Button>
      )}
      <Button size="small" aria-label={props.clearLabel} onClick={() => {
        if (saved === '') {
          return;
        }
        props.onClear();
      }}
      >
        Clear
      </Button>
    </div>
  );
};

const GroupEditor = (props: {
  supplier: any;
  group: any;
  marginOrder: MarginOrder;
  isLoading?: boolean;
  onSaveOverride: (group: any, supplier: any, margin: string) => Promise<unknown>;
  onSaveCgOverride: (group: any, supplier: any, margin: string) => Promise<unknown>;
  onSavePack: (group: any, supplier: any, packCount: string, margin: string) => Promise<unknown>;
  onSaveUnit: (group: any, supplier: any, amount: string, unitOfMeasure: string, margin: string) => Promise<unknown>;
  onSaveSourceChoice: (group: any, supplier: any, source: string) => Promise<unknown>;
}) => {
  const { supplier, group, marginOrder, onSaveOverride, onSaveCgOverride, onSavePack, onSaveUnit, onSaveSourceChoice } = props;
  const override = supplier && group ? overrideFor(group, supplier) : null;
  const cgOverride = supplier && group ? cgOverrideFor(group, supplier) : null;
  const packOverrides = supplier && group
    ? rowsForSupplier(group.intersection_pack_overrides, supplier)
    : [];
  const unitOverrides = supplier && group
    ? rowsForSupplier(group.intersection_unit_overrides, supplier)
    : [];
  const choice = supplier && group ? sourceChoiceRow(group, supplier) : null;
  const storedSource = choice && (choice.pack_source === SOURCE_SUPPLIER || choice.pack_source === SOURCE_CG)
    ? choice.pack_source
    : null;
  const shownSource = storedSource || marginOrder.pack_source_first;
  const showSource = supplier && group && (
    overlappingKeysWithoutOverride(supplier.pack_margins, group.pack_margins, packOverrides, packKeyOf)
    || overlappingKeysWithoutOverride(supplier.unit_margins, group.unit_margins, unitOverrides, unitKeyOf)
  );
  const [editorOpen, setEditorOpen] = useState(true);
  const [kind, setKind] = useState('pack');
  const [draft, setDraft] = useState('');
  const [packCount, setPackCount] = useState('');
  const [amount, setAmount] = useState('');
  const [unitOfMeasure, setUnitOfMeasure] = useState('floz');
  const title = supplier && group
    ? `${supplier.name} × ${group.number} ${group.name}`
    : 'Click a cell';

  useEffect(() => {
    setKind('pack');
    setDraft('');
    setPackCount('');
    setAmount('');
  }, [supplier && supplier.uuid, group && group.uuid, override && override.margin, cgOverride && cgOverride.margin]);

  const saveDraft = () => {
    if (kind === 'pack') {
      return onSavePack(group, supplier, packCount, draft);
    }
    return onSaveUnit(group, supplier, amount, unitOfMeasure, draft);
  };

  return (
    <div style={{ flexShrink: 0, marginBottom: 8, border: '1px solid #cfd8dc', background: '#fff' }}>
      <button type="button" onClick={() => setEditorOpen((open) => !open)} style={barButtonStyle}>
        {editorOpen ? '▾' : '▸'} {title}
        {props.isLoading ? <PanelSpinner label="Loading intersection margins" /> : null}
      </button>
      {editorOpen ? (
        <div style={{ padding: 12 }}>
          {supplier && group ? (
            <div>
              {showSource ? (
                <div data-margin-source-choice="" style={{ marginBottom: 12 }}>
                  <Typography variant="subtitle2">Margin source</Typography>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2">supplier</Typography>
                    <Switch
                      color="primary"
                      checked={shownSource === SOURCE_CG}
                      onChange={(event) => onSaveSourceChoice(
                        group,
                        supplier,
                        event.target.checked ? SOURCE_CG : SOURCE_SUPPLIER,
                      )}
                      inputProps={{ 'aria-label': 'Margin source' }}
                    />
                    <Typography variant="body2">commodity group</Typography>
                  </div>
                </div>
              ) : null}
              <OverrideLine
                label="supp ov"
                margin={override && override.margin}
                saveLabel="Supplier margin override"
                saveButtonLabel="Save supplier margin override"
                clearLabel="Clear supplier margin override"
                onSave={(value) => onSaveOverride(group, supplier, value)}
                onClear={() => onSaveOverride(group, supplier, '')}
              />
              <OverrideLine
                label="cg ov"
                margin={cgOverride && cgOverride.margin}
                saveLabel="Commodity group margin override"
                clearLabel="Clear commodity group margin override"
                onSave={(value) => onSaveCgOverride(group, supplier, value)}
                onClear={() => onSaveCgOverride(group, supplier, '')}
              />
              {packOverrides.map((item) => (
                <OverrideLine
                  key={`pack-${item.pack_count}`}
                  label={`${item.pack_count}pk`}
                  margin={item.margin}
                  saveLabel={`Pack override ${item.pack_count}`}
                  clearLabel={`Clear pack override ${item.pack_count}`}
                  onSave={(value) => onSavePack(group, supplier, String(item.pack_count), value)}
                  onClear={() => onSavePack(group, supplier, String(item.pack_count), '')}
                />
              ))}
              {unitOverrides.map((item) => (
                <OverrideLine
                  key={`unit-${item.amount}-${item.unit_of_measure}`}
                  label={`${displayMargin(item.amount)}${item.unit_of_measure}`}
                  margin={item.margin}
                  saveLabel={`Unit override ${displayMargin(item.amount)} ${item.unit_of_measure}`}
                  clearLabel={`Clear unit override ${displayMargin(item.amount)} ${item.unit_of_measure}`}
                  onSave={(value) => onSaveUnit(group, supplier, item.amount, item.unit_of_measure, value)}
                  onClear={() => onSaveUnit(group, supplier, item.amount, item.unit_of_measure, '')}
                />
              ))}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <TextField
                  select
                  value={kind}
                  inputProps={{ 'aria-label': 'Override kind' }}
                  onChange={(event) => setKind(event.target.value)}
                  style={{ width: 180 }}
                >
                  <MenuItem value="pack">pack</MenuItem>
                  <MenuItem value="unit">unit</MenuItem>
                </TextField>
                {kind === 'pack' ? (
                  <TextField
                    label="Pack count"
                    value={packCount}
                    inputProps={{ 'aria-label': 'Intersection pack count' }}
                    onChange={(event) => setPackCount((current) => wholePackCount(event.target.value, current))}
                    style={{ width: 120 }}
                  />
                ) : null}
                {kind === 'pack' ? (
                  <TextField
                    value="PK"
                    InputProps={{ readOnly: true }}
                    style={{ width: 56 }}
                  />
                ) : null}
                {kind === 'unit' ? (
                  <TextField
                    label="Amount"
                    value={amount}
                    inputProps={{ 'aria-label': 'Intersection unit amount' }}
                    onChange={(event) => setAmount((current) => unitAmount(event.target.value, current))}
                    style={{ width: 120 }}
                  />
                ) : null}
                {kind === 'unit' ? (
                  <TextField
                    select
                    value={unitOfMeasure}
                    inputProps={{ 'aria-label': 'Intersection unit' }}
                    onChange={(event) => setUnitOfMeasure(event.target.value)}
                    style={{ width: 100 }}
                  >
                    {UNITS_OF_MEASURE.map((unit) => (
                      <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                    ))}
                  </TextField>
                ) : null}
                <TextField
                  label="Margin %"
                  value={draft}
                  inputProps={{ 'aria-label': 'Intersection margin' }}
                  onChange={(event) => setDraft((current) => marginValue(event.target.value, current))}
                  style={{ width: 120 }}
                />
                <Button
                  size="small"
                  color="primary"
                  aria-label="Save intersection override"
                  onClick={saveDraft}
                >
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <Typography variant="body2">Click a cell.</Typography>
          )}
        </div>
      ) : null}
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    userData: state.userLocalData,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    showToast: (toast: Toast) => dispatch(enqueueSnackbar(toast)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(MarginByCgScreen);
